/* eslint-disable @typescript-eslint/no-explicit-any */
import { IdentityProvider } from 'types/types/IdentityProvider'
import { ISFUser } from '../../types/interfaces/ISFUser'
import { hjbcAuthApiSlice } from '../apiSlice'
import { AccountType, GenderIdentity } from 'types/interfaces/IPersonalDetails'

interface CreateUserRequest {
  email: string
  terms?: boolean
  newsletter?: boolean
  id: string
  identityProvider?: IdentityProvider
  firstName?: string
  lastName?: string
  phoneNum?: string
  dob?: string
  gender?: GenderIdentity
}

interface CreateUserResponse {
  success: boolean
  res: ISFUser
}

export interface GetFanzaUser {
  fanzaRedirectUrl: string
  newsletter: boolean
  seenMarketingMessage: boolean
}

export interface UpdateUserRequest {
  accountId?: string
  contactId?: string
  id?: string
  identityProvider?: IdentityProvider
  firstName?: string
  lastName?: string
  gender?: GenderIdentity
  toptixId?: string
  phoneNum?: string
  postalCode?: string
  city?: string
  cityHebName?: string
  street?: string
  buildingNumber?: string
  isForeignAddress?: boolean
  type?: AccountType
  dob?: string
  email?: string
  hasOptedOutOfEmail?: boolean
  doNotCall?: boolean
  description?: string
  playerImage?: number
  seenMarketingMessage?: boolean
  newsletter?: boolean
}

interface UpdateUserResponse {
  res: ISFUser
  success: boolean
}

interface UpdateUserByToptixIdRequest {
  toptixId?: string
  id: string
  identityProvider?: IdentityProvider
  email?: string
  terms?: boolean
  newsletter?: boolean
  firstName?: string
  lastName?: string
  phoneNum?: string
  dob?: string
  gender?: GenderIdentity
  seenMarketingMessage?: boolean
}

export interface Relationships {
  name: string
  type: string
  dob: string
  playerImage: number
  gender?: GenderIdentity
}

export interface TransactionHistory {
  transactionId: string
  paymentDate: string
  seasonTickets: {
    name: string
    price: number
    operation: 'Sale' | 'Return'
  }[]
  tickets: {
    name: string
    price: number
    operation: 'Sale' | 'Return'
  }[]
}

export type SeasonTicket = {
  name: string
  ownerAccount: string | undefined
  ownerAccountHjbcId: string | undefined
  seatArea: string | undefined
  seatRow: number | undefined
  seatNumber: number | undefined
  eventPackageStartDate: Date
  eventPackageEndDate: Date
  eventPackageIsParking: boolean
  price: number
}

export type SeasonTicketsResponse = {
  currentOwnerSeasonTickets: SeasonTicket[]
  pastOwnerSeasonTickets: SeasonTicket[]
  seasonTicketsPurchaserAccountByPersonAccountId: SeasonTicket[]
}

const urlPrefix = 'user'

export const userApiSlice = hjbcAuthApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSfUserById: builder.query<ISFUser, void>({
      query: () => urlPrefix,
    }),
    getSfUser: builder.query<ISFUser, { toptixId?: string; id?: string }>({
      query: ({ toptixId, id }) => ({
        url: urlPrefix,
        method: 'GET',
        params: { toptixId, id },
      }),
      providesTags: [],
    }),
    getFanzaUser: builder.query<GetFanzaUser, void>({
      query: () => ({
        url: `${urlPrefix}/fanza`,
        method: 'GET',
      }),
      providesTags: [],
    }),
    createSfUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: (body) => ({
        url: urlPrefix,
        method: 'POST',
        body,
      }),
      transformResponse: (data: CreateUserResponse) => data,
    }),
    updateSfUser: builder.mutation<UpdateUserResponse, UpdateUserRequest>({
      query: (body) => ({
        url: urlPrefix,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: UpdateUserResponse) => data,
    }),
    updateSfUserByToptixId: builder.mutation<
      UpdateUserResponse,
      UpdateUserByToptixIdRequest
    >({
      query: (body) => ({
        url: `${urlPrefix}/toptixId`,
        method: 'PATCH',
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        if ('data' in baseQueryReturnValue) {
          return baseQueryReturnValue.data
        }
        return baseQueryReturnValue
      },
    }),
    getSfUserRelationships: builder.query<Relationships[], void>({
      query: () => ({
        url: `${urlPrefix}/relationships`,
        method: 'GET',
      }),
      providesTags: [],
    }),
    getSeasonTickets: builder.query<SeasonTicketsResponse, void>({
      query: () => ({
        url: `transaction/season-tickets`,
        method: 'GET',
      }),
      providesTags: [],
    }),
    getTransactions: builder.query<TransactionHistory[], void>({
      query: () => ({
        url: `transaction/history`,
        method: 'GET',
      }),
      providesTags: [],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetSfUserQuery,
  useLazyGetFanzaUserQuery,
  useLazyGetSfUserQuery,
  useGetSfUserByIdQuery,
  useCreateSfUserMutation,
  useUpdateSfUserMutation,
  useUpdateSfUserByToptixIdMutation,
  useGetSfUserRelationshipsQuery,
  useGetSeasonTicketsQuery,
  useGetTransactionsQuery,
} = userApiSlice

export const { getSfUser, getFanzaUser } = userApiSlice.endpoints
